import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Alerts.scss";

const ALERT_VARIANTS = {
  unsavedChanges: {
    title: "Зберегти зміни?",
    text: "У вас є незбережені зміни. Хочете зберегти їх перед виходом?",
    primaryText: "Зберегти",
    secondaryText: "Не зберігати",
    cancelText: "Скасувати",
    tone: "default",
  },

  deleteListing: {
    title: "Видалити оголошення?",
    text: "Цю дію неможливо відмінити.",
    primaryText: "Видалити",
    secondaryText: null,
    cancelText: "Скасувати",
    tone: "danger",
  },

  archiveListing: {
    title: "Архівувати оголошення?",
    text: "Оголошення буде приховане зі стрічки, але залишиться у вас.",
    primaryText: "Архівувати",
    secondaryText: null,
    cancelText: "Скасувати",
    tone: "default",
  },

  headlineLoginRequired: {
    title: "Потрібна авторизація",
    text: "Щоб написати, увійдіть у свій акаунт. Перейти до авторизації?",
    primaryText: "Авторизуватись",
    secondaryText: null,
    cancelText: null,
    tone: "default",
  },
};

const CLOSE_ANIM_MS = 260;

const Alerts = ({
  open,
  variant = "unsavedChanges",

  title,
  text,
  children,

  primaryText,
  secondaryText,
  cancelText,

  onPrimary,
  onSecondary,
  onCancel,

  onClose,

  className = "",
  lockBodyScroll = true,
  closeOnBackdrop = true,
  closeOnEsc = true,

  tone,
}) => {
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);

  const cardRef = useRef(null);

  const data = useMemo(() => ALERT_VARIANTS[variant] || null, [variant]);

  const finalTitle = title ?? data?.title ?? "";
  const finalText = text ?? data?.text ?? "";
  const finalPrimaryText = primaryText ?? data?.primaryText ?? null;
  const finalSecondaryText = secondaryText ?? data?.secondaryText ?? null;
  const finalCancelText = cancelText ?? data?.cancelText ?? "Закрити";
  const finalTone = tone ?? data?.tone ?? "default";

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
    } else {
      if (!mounted) return;
      setClosing(true);
      const t = setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, CLOSE_ANIM_MS);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!lockBodyScroll) return;
    if (!mounted) return;

    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [mounted, lockBodyScroll]);

  useEffect(() => {
    if (!mounted) return;
    if (!closeOnEsc) return;

    const onKey = (e) => {
      if (e.key === "Escape") {
        if (onClose) onClose();
        else if (onCancel) onCancel();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mounted, closeOnEsc, onClose, onCancel]);

  useEffect(() => {
    if (!mounted) return;
    const t = setTimeout(() => {
      if (cardRef.current) cardRef.current.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [mounted]);

  if (!mounted) return null;

  const handleBackdrop = () => {
    if (!closeOnBackdrop) return;
    if (closing) return;
    if (onClose) onClose();
    else if (onCancel) onCancel();
  };

  const handleCloseX = () => {
    if (closing) return;
    if (onClose) onClose();
    else if (onCancel) onCancel();
  };

  return (
    <div
      className={`alertsModal ${closing ? "is-closing" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={finalTitle || "Повідомлення"}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleBackdrop();
      }}
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        className={`alertsCard ${className} ${closing ? "is-closing" : ""} ${
          finalTone === "danger" ? "alertsCard--danger" : ""
        }`}
      >
        <button className="alertsClose" onClick={handleCloseX} aria-label="Закрити">
          ✕
        </button>

        {!!finalTitle && <div className="alertsTitle">{finalTitle}</div>}

        {(!!finalText || !!children) && (
          <div className="alertsBody">
            {!!finalText && <div className="alertsText">{finalText}</div>}
            {children}
          </div>
        )}

        <div className="alertsActions">
          {!!finalCancelText && (
            <button
              className="alertsBtn alertsBtn--ghost"
              onClick={() => {
                if (closing) return;
                if (onCancel) onCancel();
                else if (onClose) onClose();
              }}
            >
              {finalCancelText}
            </button>
          )}

          {!!finalSecondaryText && (
            <button
              className="alertsBtn alertsBtn--secondary"
              onClick={() => {
                if (closing) return;
                if (onSecondary) onSecondary();
              }}
            >
              {finalSecondaryText}
            </button>
          )}

          {!!finalPrimaryText && (
            <button
              className={`alertsBtn alertsBtn--primary ${
                finalTone === "danger" ? "alertsBtn--danger" : ""
              }`}
              onClick={() => {
                if (closing) return;
                if (onPrimary) onPrimary();
              }}
            >
              {finalPrimaryText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Alerts;
export { ALERT_VARIANTS };